import { DefaultPrimaryButton, Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { LuCheck, LuChevronDown, LuChevronUp, LuPrinter } from 'react-icons/lu';

import PlusMinusNumberInput from '../../../components/plus-minus-number-input/plus-minus-number-input.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import usePrintLabels from './print-labels.controller';

const ID = 'print-labels';

const PrintLabels = () => {
    const viewProps = usePrintLabels();

    return (
        <WizardScreenContainer
            id={ID}
            footer={{
                onBack: viewProps.onBack,
                onSubmit: viewProps.onSubmit
            }}>
            <div className='flex w-full flex-col items-start justify-start gap-3'>
                <div className='text-foreground-default justify-start text-base font-medium'>
                    {CommonMessage.DETAIL.DETAILS_OF(CommonMessage.OBJECTS.TRANSACTION.DEFAULT)}
                </div>

                {viewProps.transaction && (
                    <div className='bg-background-surface flex w-full flex-col items-start justify-start self-stretch rounded-xl'>
                        <button
                            type='button'
                            aria-expanded={viewProps.showTransactionDetails}
                            onClick={viewProps.onToggleTransactionDetails}
                            className='inline-flex w-full items-center justify-between gap-3 self-stretch px-4 py-3 text-left'>
                            <div className='text-foreground-default justify-start text-base font-medium'>
                                {CommonMessage.DETAIL.DETAILS_OF(CommonMessage.OBJECTS.TRANSACTION.DEFAULT)}
                            </div>
                            {viewProps.showTransactionDetails ? (
                                <LuChevronUp className='text-brand-muted size-5 shrink-0' />
                            ) : (
                                <LuChevronDown className='text-brand-muted size-5 shrink-0' />
                            )}
                        </button>

                        {viewProps.visibleTransactionDetails.length > 0 && (
                            <div className='border-border-default flex w-full flex-col items-start justify-start gap-4 self-stretch border-t px-4 py-3'>
                                {viewProps.visibleTransactionDetails.map((detail) => (
                                    <div
                                        key={detail.key}
                                        className='inline-flex min-h-6 items-start justify-between gap-4 self-stretch'>
                                        <div className='text-brand-muted justify-start text-base font-medium'>{detail.label}</div>
                                        <div className='text-foreground-default text-right text-base font-medium wrap-break-word'>{detail.value}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.OBJECTS.PRINTER.DEFAULT}</div>

                {viewProps.isLoading && (
                    <div className='text-brand-muted inline-flex items-center gap-2 text-sm font-medium'>
                        <Spinner className='size-4' />
                        {CommonMessage.OBJECTS.PRINTER.DEFAULT}
                    </div>
                )}

                {!viewProps.isLoading && viewProps.availablePrinters.length === 0 && (
                    <div className='bg-background-surface text-brand-muted inline-flex w-full items-start justify-start self-stretch rounded-xl p-4 text-base font-medium'>
                        {CommonMessage.OBJECTS.PRINTER.NO_DEFAULT_PRINTER}
                    </div>
                )}

                {!viewProps.isLoading && viewProps.availablePrinters.length > 0 && (
                    <div className='bg-background-surface flex w-full flex-col items-start justify-start self-stretch rounded-xl'>
                        {viewProps.availablePrinters.map((iot, index) => {
                            const isSelected = viewProps.printer?.id === iot.id;
                            return (
                                <button
                                    type='button'
                                    key={iot.id}
                                    onClick={() => viewProps.onSelectPrinter(iot)}
                                    className={[
                                        'inline-flex w-full items-center justify-between gap-3 px-4 py-3 text-left',
                                        index === 0 ? 'rounded-t-xl' : '',
                                        index === viewProps.availablePrinters.length - 1 ? 'rounded-b-xl' : 'border-border-default border-b'
                                    ].join(' ')}>
                                    <div className='flex min-w-0 flex-1 items-center gap-3'>
                                        <LuPrinter className='text-brand-muted size-5 shrink-0' />
                                        <div
                                            className='text-foreground-default min-w-0 flex-1 justify-start truncate text-base font-medium'
                                            title={iot.name}>
                                            {iot.name}
                                        </div>
                                    </div>
                                    {isSelected && <LuCheck className='text-brand-primary size-5 shrink-0' />}
                                </button>
                            );
                        })}
                    </div>
                )}

                {viewProps.printer && (
                    <>
                        <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.OBJECTS.PRINTER.COPIES}</div>
                        <PlusMinusNumberInput
                            min={1}
                            value={viewProps.quantity}
                            onChange={viewProps.setQuantity}
                        />
                    </>
                )}

                {viewProps.printer && (
                    <div className='bg-background-surface flex w-full flex-col items-start justify-start gap-4 self-stretch rounded-xl p-4'>
                        <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.PRINTER.PRINT_TO_CONTINUE}</div>

                        {viewProps.canPrint && (
                            <DefaultPrimaryButton
                                isLoading={viewProps.printing}
                                text={
                                    <>
                                        {!viewProps.printing && <LuPrinter className='size-6 pe-2' />}
                                        <div className='justify-start'>{CommonMessage.OBJECTS.PRINTER.PRINT_LABELS}</div>
                                    </>
                                }
                                onClick={viewProps.onPrint}
                                disabled={viewProps.printing}
                            />
                        )}
                    </div>
                )}
            </div>
        </WizardScreenContainer>
    );
};

export default PrintLabels;
